import React, { useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { KeyRound, CheckCircle2, AlertCircle } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { formatApiErrorDetail } from "../lib/api";
import PasswordStrengthMeter from "../components/PasswordStrengthMeter";
import { tokenFromLocation } from "../lib/tokenFromLocation";

export default function ResetPassword() {
  const { t } = useTranslation("auth");
  // Token travels in the URL fragment (never sent to servers/analytics);
  // the path param remains as a fallback for links from older emails.
  const { token: pathToken } = useParams();
  const token = tokenFromLocation(useLocation(), pathToken);
  const navigate = useNavigate();
  const { resetPassword } = useAuth();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError(t("errors.password_min"));
      return;
    }
    if (password !== confirmPassword) {
      setError(t("errors.password_mismatch"));
      return;
    }
    setSubmitting(true);
    try {
      await resetPassword({ token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 1500);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || t("errors.invalid_token"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center px-4 py-12 bg-zinc-50" data-testid="reset-password-page">
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-2xl p-8 shadow-sm">
        <Link to="/" className="inline-block mb-6 hover:opacity-80 transition-opacity" data-testid="reset-password-brand-link" aria-label="QENVA · The Silent Guardian">
          <img
            src="/brand/v2-primary-logo.png"
            alt="QENVA · The Silent Guardian"
            draggable={false}
            className="h-8 w-auto block select-none"
            style={{ objectFit: "contain" }}
            data-testid="reset-password-brand-logo"
          />
        </Link>

        <div className="w-12 h-12 rounded-xl bg-zinc-950 grid place-items-center mb-5">
          <KeyRound size={20} className="text-white" />
        </div>

        {!done && (
          <>
            <h1 className="font-display text-2xl font-semibold text-zinc-950 tracking-tight">
              {t("reset_password.title")}
            </h1>
            <p className="text-sm text-zinc-600 mt-2">{t("reset_password.subtitle")}</p>

            <form onSubmit={handleSubmit} className="mt-6">
              <label className="text-xs text-zinc-500" htmlFor="reset-password-input">
                {t("reset_password.password_label")}
              </label>
              <input
                id="reset-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                maxLength={128}
                required
                data-testid="reset-password-input"
                className="w-full mt-1 h-11 px-3 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-300"
              />
              <PasswordStrengthMeter password={password} />

              <label className="text-xs text-zinc-500 mt-4 block" htmlFor="reset-password-confirm-input">
                {t("fields.confirm_password")}
              </label>
              <input
                id="reset-password-confirm-input"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                minLength={8}
                maxLength={128}
                required
                data-testid="reset-password-confirm-input"
                className="w-full mt-1 h-11 px-3 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-300"
              />
              {confirmPassword.length > 0 && confirmPassword !== password && (
                <p className="text-[11px] text-rose-700 mt-1" data-testid="reset-password-confirm-error">
                  {t("errors.password_mismatch")}
                </p>
              )}
              {error && (
                <div className="flex items-start gap-2 text-rose-700 mt-3 text-sm" data-testid="reset-password-error">
                  <AlertCircle size={15} className="mt-0.5 shrink-0" />
                  <p>{error}</p>
                </div>
              )}
              <Button
                type="submit"
                disabled={submitting}
                data-testid="reset-password-submit"
                className="w-full mt-5 h-11 bg-zinc-950 hover:bg-zinc-800 text-white rounded-lg disabled:opacity-50"
              >
                {t("reset_password.submit")}
              </Button>
            </form>
          </>
        )}

        {done && (
          <div className="text-center" data-testid="reset-password-success">
            <CheckCircle2 className="mx-auto text-emerald-500" size={28} />
            <p className="text-base font-semibold text-zinc-950 mt-3">{t("reset_password.success_title")}</p>
            <p className="text-sm text-zinc-500 mt-1">{t("reset_password.success_subtitle")}</p>
            <Link to="/login" className="text-sm text-zinc-950 underline mt-4 inline-block">
              {t("reset_password.go_to_login")}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
